import type { MediaAsset } from '@/domain/types';
import { requireSupabase } from '@/lib/supabase';

type MediaRow = {
  id: string;
  bucket: string;
  storage_path: string;
  original_file_name: string;
  display_name: string;
  file_extension: string;
  mime_type: string | null;
  file_size: number | null;
  visibility: 'public' | 'private';
  asset_type: 'media' | 'product_file';
  created_at: string;
  product_files?: Array<{ products?: { name: string } | null }> | null;
};

export type MediaUploadKind = 'public-media' | 'product-files';

function fileExtension(name: string) {
  const parts = name.split('.');
  return parts.length > 1 ? parts.pop()?.toLowerCase() ?? '' : '';
}

type SupabaseClient = ReturnType<typeof requireSupabase>;

function mapMedia(row: MediaRow, supabase: SupabaseClient): MediaAsset {
  const publicUrl = row.visibility === 'public'
    ? supabase.storage.from(row.bucket).getPublicUrl(row.storage_path).data.publicUrl
    : undefined;

  return {
    id: row.id,
    bucket: row.bucket,
    storagePath: row.storage_path,
    originalFileName: row.original_file_name,
    displayName: row.display_name,
    fileExtension: row.file_extension,
    mimeType: row.mime_type,
    fileSize: row.file_size,
    visibility: row.visibility,
    assetType: row.asset_type,
    publicUrl,
    productName: row.product_files?.[0]?.products?.name ?? undefined,
    createdAt: row.created_at,
  };
}

export async function listMediaAssets() {
  const supabase = requireSupabase();
  const { data, error } = await supabase
    .from('media_assets')
    .select('*, product_files!media_asset_id(products!product_id(name))')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return (data as MediaRow[]).map((row) => mapMedia(row, supabase));
}

export async function uploadMediaAsset(file: File, kind: MediaUploadKind) {
  const supabase = requireSupabase();
  const extension = fileExtension(file.name);
  const generatedName = `${crypto.randomUUID()}${extension ? `.${extension}` : ''}`;
  const assetType = kind === 'product-files' ? 'product_file' : 'media';
  const path = `${assetType}/${new Date().getFullYear()}/${generatedName}`;

  const { error: uploadError } = await supabase.storage
    .from(kind)
    .upload(path, file, {
      contentType: file.type || undefined,
      upsert: false,
    });

  if (uploadError) throw uploadError;

  const { data, error } = await supabase
    .from('media_assets')
    .insert({
      bucket: kind,
      storage_path: path,
      original_file_name: file.name,
      display_name: file.name,
      file_extension: extension,
      mime_type: file.type || null,
      file_size: file.size,
      visibility: kind === 'public-media' ? 'public' : 'private',
      asset_type: assetType,
    })
    .select('*')
    .single();

  if (error) throw error;
  return mapMedia(data as MediaRow, supabase);
}

export async function listUserAvatars(userId: string): Promise<MediaAsset[]> {
  const supabase = requireSupabase();
  const { data, error } = await supabase
    .from('media_assets')
    .select('*')
    .like('storage_path', `avatars/${userId}/%`)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return ((data as MediaRow[]) ?? []).map((row) => mapMedia(row, supabase));
}

export async function uploadAvatar(userId: string, file: File): Promise<MediaAsset> {
  const supabase = requireSupabase();
  const extension = fileExtension(file.name);
  const generatedName = `${crypto.randomUUID()}${extension ? `.${extension}` : ''}`;
  const path = `avatars/${userId}/${generatedName}`;

  // Enforce max 2 — delete oldest if already at limit
  const { data: existing, error: listError } = await supabase
    .from('media_assets')
    .select('id, storage_path, bucket')
    .like('storage_path', `avatars/${userId}/%`)
    .order('created_at', { ascending: true });
  if (listError) throw listError;

  if (existing && existing.length >= 2) {
    const oldest = existing[0];
    await supabase.storage.from(oldest.bucket).remove([oldest.storage_path]);
    await supabase.from('media_assets').delete().eq('id', oldest.id);
  }

  const { error: uploadError } = await supabase.storage
    .from('public-media')
    .upload(path, file, { contentType: file.type || undefined, upsert: false });
  if (uploadError) throw uploadError;

  const { data, error: insertError } = await supabase
    .from('media_assets')
    .insert({
      bucket: 'public-media',
      storage_path: path,
      original_file_name: file.name,
      display_name: file.name,
      file_extension: extension,
      mime_type: file.type || null,
      file_size: file.size,
      visibility: 'public',
      asset_type: 'media',
    })
    .select('*')
    .single();
  if (insertError) throw insertError;
  return mapMedia(data as MediaRow, supabase);
}

export async function deleteMediaAsset(asset: MediaAsset) {
  const supabase = requireSupabase();
  const { error: storageError } = await supabase.storage
    .from(asset.bucket)
    .remove([asset.storagePath]);

  if (storageError) throw storageError;

  const { error } = await supabase
    .from('media_assets')
    .delete()
    .eq('id', asset.id);

  if (error) throw error;
}
